import { NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DatepickerHeaderComponent } from '@blaze-shared/shared-feature';
import { AutoSlashDateInputDirective } from '@blaze-shared/shared-ui';
import { DATE_FORMAT_TO_FNS_MAP, isNullish, isValidDate } from '@blaze-shared/shared-util';

import { DateFnsAdapter } from '../../../../datetime-input/custom-date-fns-adapter';
import { DynamicFormatDate } from '../../../../datetime-input/dynamic-format-date';
import { InlineEditorConfig } from '../../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'blaze-inline-date',
  templateUrl: './inline-date.component.html',
  styleUrls: ['../datetime-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, AutoSlashDateInputDirective, NgStyle, MatMenuModule, MatDatepickerModule],
  providers: [
    DynamicFormatDate,
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useExisting: DynamicFormatDate,
    },
  ],
})
export class InlineDateComponent implements AfterViewInit, OnDestroy {
  private readonly dynamicFormatDate = inject(DynamicFormatDate);

  config = input<InlineEditorConfig>();
  value = input<unknown>();

  pickerClosed = output();
  pickerOpened = output();
  valueChanged = output<string | null>();

  dateInput = viewChild('dateInput', { read: ElementRef });
  datepicker = viewChild<MatDatepicker<Date>>('datepicker');

  private readonly dateValue = computed<Date | null | undefined>(() => {
    const value = this.value();
    if (value === null || value === undefined || !value) {
      return null;
    }

    if (typeof value === 'string') {
      return this.transformStringDateToDate(value);
    }
    throw new TypeError(
      `CellDateComponent: value is expected to be string | null | undefined, got ${typeof value}`,
    );
  });

  protected datepickerHeader = DatepickerHeaderComponent;
  protected currentDateFormat = DATE_FORMAT_TO_FNS_MAP['MM/DD/YYYY'];

  protected internalValue = linkedSignal<Date | null>(() => {
    const dateValue = this.dateValue();
    return dateValue ? new Date(dateValue) : null;
  });

  constructor() {
    this.dynamicFormatDate.setDisplayFormat({ dateInput: this.currentDateFormat });
  }

  ngAfterViewInit(): void {
    const datepicker = this.datepicker();
    if (!datepicker) {
      throw new Error('Expected datepicker to be defined');
    }
    datepicker.closedStream.pipe(untilDestroyed(this)).subscribe(() => {
      this.pickerClosed.emit();
    });
  }

  ngOnDestroy(): void {
    this.closeDatePicker();
  }

  openDatePicker(): void {
    const datepicker = this.datepicker();
    if (!datepicker) {
      throw new Error('Expected datepicker to be defined');
    }
    if (datepicker.opened) {
      return;
    }
    datepicker.open();

    setTimeout(() => {
      if (!datepicker.opened) {
        return;
      }

      const datepickerHtml = document.getElementById(datepicker.id);
      const datepickerContentHtml = datepickerHtml?.closest('mat-datepicker-content');

      if (datepickerContentHtml) {
        datepickerContentHtml.classList.add('b-dti');
      }
    });
  }

  closeDatePicker(): void {
    this.datepicker()?.close();
  }

  protected pickerClosedFn(): void {
    const dateInput = this.dateInput();
    dateInput?.nativeElement.select();
  }

  protected onDateChange(value: Date | null): void {
    if (isNullish(value)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    const inputValue = this.dateInput()?.nativeElement.value;
    if (!inputValue.split('/')[2] && inputValue.length > 0) {
      return;
    }

    let newValue: Date | null = null;
    if (value instanceof Date) {
      newValue = this.transformStringDateToDate(this.prepareDateToOutput(value));
    } else {
      throw new TypeError(
        `InlineDateComponent: value is expected to be Date | null, got ${typeof value}`,
      );
    }

    if (!newValue || !isValidDate(newValue)) {
      this.internalValue.set(null);
      this.valueChanged.emit(null);
      return;
    }

    if (document.activeElement !== inputValue.nativeElement) {
      this.internalValue.set(newValue);
    }
    this.valueChanged.emit(this.prepareDateToOutput(value));
  }

  private prepareDateToOutput(date: string | Date): string {
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    const dateDate = date.getDate().toString().padStart(2, '0');
    const dateMonth = (date.getMonth() + 1).toString().padStart(2, '0');
    const dateYear = date.getFullYear().toString().padStart(4, '0');
    const valueToEmit = `${dateYear}-${dateMonth}-${dateDate}`;
    return valueToEmit;
  }

  private transformStringDateToDate(value: string): Date {
    const tzOffset = new Date(value + 'T00:00:00').getTimezoneOffset();
    const tzOffsetHours = Math.floor(tzOffset / 60)
      .toString()
      .padStart(2, '0');
    const tzOffsetMinutes = (tzOffset % 60).toString().padStart(2, '0');

    return new Date(
      value + (tzOffset > 0 ? `T${tzOffsetHours}:${tzOffsetMinutes}:00` : 'T00:00:00'),
    );
  }
}
